import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CircleNotch, CurrencyDollar, MouseSimple, ArrowsClockwise, Users, Heart as HeartIcon, Sneaker, CreditCard, ChartLineUp, DownloadSimple, ArrowClockwise, PaperPlaneTilt } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import ScrapingTab from './admin/ScrapingTab';
import LinksTab from './admin/LinksTab';
import SeedTab from './admin/SeedTab';
import AwinTab from './admin/AwinTab';
import ApplicationsTab from './admin/ApplicationsTab';
import NetworksTab from './admin/NetworksTab';
import AffiliateSyncTab from './admin/AffiliateSyncTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState({ users: [], customer_data: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchParams] = useSearchParams();
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [emailForm, setEmailForm] = useState({ subject: '', html_content: '', recipient_type: 'all' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [affiliateSettings, setAffiliateSettings] = useState([]);
  const [affiliateForm, setAffiliateForm] = useState({ store_id: '', affiliate_tag: '', network: '' });
  const [allStores, setAllStores] = useState([]);

  // "Add new store" mini-form (in the Affiliate tab)
  const [newStoreForm, setNewStoreForm] = useState({
    store_id: '', name: '', region: 'UK', currency: 'GBP',
    affiliate_network: 'awin', affiliate_advertiser_id: '',
    website_url: '', search_url: '',
  });
  const [savingNewStore, setSavingNewStore] = useState(false);
  const [newStoreMsg, setNewStoreMsg] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [schedulerConfig, setSchedulerConfig] = useState({ enabled: false, scrape_interval_hours: 24, verify_interval_hours: 48 });
  const [linkAudit, setLinkAudit] = useState(null);
  const [contactsData, setContactsData] = useState({ items: [], total: 0, new_count: 0 });
  const [subscribersData, setSubscribersData] = useState({ items: [], total: 0, active_count: 0, by_source: {} });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, clicksRes, commissionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/clicks`, { withCredentials: true }),
        axios.get(`${API}/admin/commissions`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setClicks(clicksRes.data);
      setCommissions(commissionsRes.data);
      setUsers(usersRes.data);
      // Also fetch campaigns and affiliate settings
      axios.get(`${API}/admin/email/campaigns`, { withCredentials: true }).then(r => setCampaigns(r.data)).catch(() => {});
      axios.get(`${API}/admin/affiliate-settings`, { withCredentials: true }).then(r => setAffiliateSettings(r.data)).catch(() => {});
      axios.get(`${API}/stores`, { withCredentials: true }).then(r => setAllStores(r.data)).catch(() => {});
      axios.get(`${API}/admin/contacts`, { withCredentials: true }).then(r => setContactsData(r.data)).catch(() => {});
      axios.get(`${API}/admin/subscribers`, { withCredentials: true }).then(r => setSubscribersData(r.data)).catch(() => {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check payout status from URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const { data } = await axios.get(`${API}/admin/payout/status/${sessionId}`, { withCredentials: true });
          setPayoutStatus(data);
          if (data.payment_status === 'paid') { fetchData(); return; }
          setTimeout(() => pollStatus(attempts + 1), 2000);
        } catch { /* ignore */ }
      };
      pollStatus();
    }
  }, [searchParams, fetchData]);

  const handlePayout = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/payout`, { origin_url: window.location.origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Payout error');
    }
  };

  const handlePriceRefresh = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/prices/refresh`, {}, { withCredentials: true });
      setRefreshResult(data);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Refresh error'); }
    finally { setRefreshing(false); }
  };

  const handleExport = (type) => {
    window.open(`${API}/admin/export/${type}`, '_blank');
  };

  const handleScrapeAll = async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/admin/scrape/all`, {}, { withCredentials: true });
      // Poll status
      const pollInterval = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/admin/scrape/status`, { withCredentials: true });
          setScrapeStatus(data);
          if (data.status === 'complete' || data.status === 'idle') {
            clearInterval(pollInterval);
            setScraping(false);
          }
        } catch { clearInterval(pollInterval); setScraping(false); }
      }, 3000);
    } catch (e) { alert(e.response?.data?.detail || 'Scrape error'); setScraping(false); }
  };

  const handleScrapeOne = async (shoeId) => {
    try {
      const { data } = await axios.post(`${API}/admin/scrape/product/${shoeId}`, {}, { withCredentials: true });
      alert(`Scraped ${shoeId}: ${data.updated_fields.join(', ')} updated`);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleCleanupImages = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/scrape/cleanup`, {}, { withCredentials: true });
      alert(`Cleaned ${data.cleaned} bad images out of ${data.total_checked} checked`);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const [verifyStatus, setVerifyStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handleVerifyAllStores = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/scrape/verify-all-stores`, {}, { withCredentials: true });
      const pollInterval = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/admin/scrape/verify-status`, { withCredentials: true });
          setVerifyStatus(data);
          if (data.status === 'complete' || data.status === 'idle') {
            clearInterval(pollInterval);
            setVerifying(false);
          }
        } catch { clearInterval(pollInterval); setVerifying(false); }
      }, 5000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); setVerifying(false); }
  };

  const [validating, setValidating] = useState(false);
  const [validateStatus, setValidateStatus] = useState(null);

  const handleValidateAllLinks = async () => {
    setValidating(true);
    try {
      await axios.post(`${API}/admin/validate-links`, {}, { withCredentials: true });
      const pollInterval = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/admin/validate-links/status`, { withCredentials: true });
          setValidateStatus(data);
          if (data.status === 'complete' || data.status === 'idle') {
            clearInterval(pollInterval);
            setValidating(false);
          }
        } catch { clearInterval(pollInterval); setValidating(false); }
      }, 4000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); setValidating(false); }
  };

  const handleSendCampaign = async () => {
    if (!emailForm.subject || !emailForm.html_content) { alert('Fill in subject and content'); return; }
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/admin/email/campaign`, emailForm, { withCredentials: true });
      alert(data.message);
      setEmailForm({ subject: '', html_content: '', recipient_type: 'all' });
      axios.get(`${API}/admin/email/campaigns`, { withCredentials: true }).then(r => setCampaigns(r.data));
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setSendingEmail(false); }
  };

  const handleSaveAffiliate = async () => {
    if (!affiliateForm.store_id || !affiliateForm.affiliate_tag) { alert('Select a store and enter an affiliate tag'); return; }
    try {
      await axios.post(`${API}/admin/affiliate-settings`, affiliateForm, { withCredentials: true });
      axios.get(`${API}/admin/affiliate-settings`, { withCredentials: true }).then(r => setAffiliateSettings(r.data));
      setAffiliateForm({ store_id: '', affiliate_tag: '', network: '' });
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleCreateStore = async () => {
    setNewStoreMsg(null);
    const f = newStoreForm;
    if (!f.store_id || !f.name || !f.region || !f.currency) {
      setNewStoreMsg({ kind: 'err', text: 'Store ID, name, region and currency are required.' });
      return;
    }
    setSavingNewStore(true);
    try {
      const body = {
        store_id: f.store_id.trim().toLowerCase(),
        name: f.name.trim(),
        region: f.region,
        currency: f.currency,
        affiliate_network: f.affiliate_network || undefined,
        affiliate_advertiser_id: f.affiliate_advertiser_id.trim() || undefined,
        website_url: f.website_url.trim() || undefined,
        search_url: f.search_url.trim() || undefined,
      };
      const { data } = await axios.post(`${API}/admin/store`, body, { withCredentials: true });
      setNewStoreMsg({
        kind: 'ok',
        text: data.created
          ? `Created store "${data.store_id}". It will now appear in the Awin tab and is ready for affiliate tracking.`
          : `Updated existing store "${data.store_id}".`,
      });
      setNewStoreForm({
        store_id: '', name: '', region: 'UK', currency: 'GBP',
        affiliate_network: 'awin', affiliate_advertiser_id: '',
        website_url: '', search_url: '',
      });
      fetchData();
    } catch (e) {
      setNewStoreMsg({ kind: 'err', text: e.response?.data?.detail || e.message });
    } finally {
      setSavingNewStore(false);
    }
  };

  const handleDeleteAffiliate = async (storeId) => {
    try {
      await axios.delete(`${API}/admin/affiliate-settings/${storeId}`, { withCredentials: true });
      setAffiliateSettings(affiliateSettings.filter(s => s.store_id !== storeId));
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const simulateConversion = async (clickId) => {
    try {
      await axios.post(`${API}/affiliate/simulate-conversion/${clickId}`, {}, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const [priceScraping, setPriceScraping] = useState(false);
  const [priceStatus, setPriceStatus] = useState(null);

  const handleScrapeRealPrices = async () => {
    setPriceScraping(true);
    try {
      await axios.post(`${API}/admin/scrape/prices`, {}, { withCredentials: true });
      const pollInterval = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/admin/scrape/price-status`, { withCredentials: true });
          setPriceStatus(data);
          if (data.status === 'complete' || data.status === 'idle') {
            clearInterval(pollInterval);
            setPriceScraping(false);
          }
        } catch { clearInterval(pollInterval); setPriceScraping(false); }
      }, 3000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); setPriceScraping(false); }
  };

  const handleSaveScheduler = async () => {
    try {
      await axios.post(`${API}/admin/scheduler`, schedulerConfig, { withCredentials: true });
      alert('Scheduler settings saved');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const fetchLinkAudit = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/link-audit`, { withCredentials: true });
      setLinkAudit(data);
    } catch (e) { console.error(e); }
  };

  // AU JS Scraping
  const [auScraping, setAuScraping] = useState(false);
  const [auStatus, setAuStatus] = useState(null);
  const handleScrapeAuStores = async () => {
    setAuScraping(true);
    try {
      await axios.post(`${API}/admin/scrape/au-stores`, {}, { withCredentials: true });
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/admin/scrape/au-status`, { withCredentials: true });
          setAuStatus(data);
          if (data.status === 'complete' || data.status === 'idle') { clearInterval(poll); setAuScraping(false); }
        } catch { clearInterval(poll); setAuScraping(false); }
      }, 5000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); setAuScraping(false); }
  };

  // Manual Store Links
  const [shoesNoStores, setShoesNoStores] = useState([]);
  const [newLink, setNewLink] = useState({ shoe_id: '', store_name: '', store_region: 'AU', price: '', currency: 'AUD', product_url: '' });
  const fetchShoesNoStores = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/shoes-without-stores`, { withCredentials: true });
      setShoesNoStores(data);
    } catch (e) { console.error(e); }
  };
  const handleAddLink = async () => {
    if (!newLink.shoe_id || !newLink.store_name || !newLink.price || !newLink.product_url) { alert('Fill all fields'); return; }
    try {
      await axios.post(`${API}/admin/store-links/add`, newLink, { withCredentials: true });
      alert('Store link added!');
      setNewLink({ shoe_id: '', store_name: '', store_region: 'AU', price: '', currency: 'AUD', product_url: '' });
      fetchShoesNoStores();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  // Seed Catalog
  const [seedingKey, setSeedingKey] = useState(null);
  const [seedResults, setSeedResults] = useState({});
  const handleRunSeed = async (key) => {
    setSeedingKey(key);
    setSeedResults(prev => ({ ...prev, [key]: { status: 'running' } }));
    try {
      const { data } = await axios.post(`${API}/admin/seed/${key}`, {}, { withCredentials: true, timeout: 60000 });
      setSeedResults(prev => ({ ...prev, [key]: { status: 'ok', ...data, at: new Date().toLocaleTimeString() } }));
      fetchData(); // refresh shoe count
    } catch (e) {
      setSeedResults(prev => ({ ...prev, [key]: { status: 'error', detail: e.response?.data?.detail || e.message } }));
    } finally {
      setSeedingKey(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>;

  // Chart data
  const clicksByDay = clicks.reduce((acc, c) => {
    const day = c.timestamp?.split('T')[0] || 'Unknown';
    acc[day] = (acc[day] || 0) + 1;
    return acc;
  }, {});
  const chartData = Object.entries(clicksByDay).sort().slice(-14).map(([date, count]) => ({ date: date.slice(5), clicks: count }));

  const commissionsByDay = commissions.reduce((acc, c) => {
    const day = c.created_at?.split('T')[0] || 'Unknown';
    acc[day] = (acc[day] || 0) + c.commission_amount;
    return acc;
  }, {});
  const commissionChart = Object.entries(commissionsByDay).sort().slice(-14).map(([date, amount]) => ({ date: date.slice(5), amount: parseFloat(amount.toFixed(2)) }));

  const tabs = ['overview', 'performance', 'clicks', 'commissions', 'users', 'contacts', 'subscribers', 'export', 'email', 'prices', 'scraping', 'affiliate', 'networks', 'awin', 'applications', 'scheduler', 'audit', 'links', 'seed'];

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Admin</p>
            <h1 className="font-heading font-black text-3xl tracking-tighter uppercase" data-testid="admin-title">Commission Dashboard</h1>
          </div>
          {stats?.pending_earnings > 0 && (
            <button onClick={handlePayout} className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#D62828] transition-colors flex items-center gap-2 text-sm" data-testid="payout-btn">
              <CreditCard size={18} /> Collect ${stats.pending_earnings.toFixed(2)}
            </button>
          )}
        </div>

        {payoutStatus?.payment_status === 'paid' && (
          <div className="bg-[#10B981] text-white p-4 mb-6 font-bold" data-testid="payout-success">
            Payout successful! Commissions have been marked as paid.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-tabs">
          {tabs.map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${activeTab === t ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] bg-white hover:border-[#0A0A0A]'}`} data-testid={`tab-${t}`}>
              {t}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Earnings', value: `$${stats?.total_earnings?.toFixed(2)}`, icon: <CurrencyDollar size={24} weight="duotone" className="text-[#FF3B30]" /> },
                { label: 'Pending', value: `$${stats?.pending_earnings?.toFixed(2)}`, icon: <ChartLineUp size={24} weight="duotone" className="text-[#F59E0B]" /> },
                { label: 'Total Clicks', value: stats?.total_clicks, icon: <MouseSimple size={24} weight="duotone" className="text-[#52525B]" /> },
                { label: 'Conversion Rate', value: `${stats?.conversion_rate}%`, icon: <ArrowsClockwise size={24} weight="duotone" className="text-[#10B981]" /> },
                { label: 'Total Users', value: stats?.total_users, icon: <Users size={24} weight="duotone" className="text-[#52525B]" /> },
                { label: 'Wishlists', value: stats?.total_wishlists, icon: <HeartIcon size={24} weight="duotone" className="text-[#FF3B30]" /> },
                { label: 'Conversions', value: stats?.total_conversions, icon: <CreditCard size={24} weight="duotone" className="text-[#10B981]" /> },
                { label: 'Shoes', value: stats?.total_shoes, icon: <Sneaker size={24} weight="duotone" className="text-[#52525B]" /> },
              ].map((s, i) => (
                <motion.div key={i} className="border border-[#E4E4E7] bg-white p-4 flex flex-col gap-2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {s.icon}
                  <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{s.label}</p>
                  <p className="font-heading font-black text-2xl">{s.value}</p>
                </motion.div>
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="border border-[#E4E4E7] bg-white p-6" data-testid="clicks-chart">
                <h3 className="font-heading font-bold text-lg mb-4">Affiliate Clicks (Last 14 Days)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="clicks" fill="#FF3B30" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-[#E4E4E7] bg-white p-6" data-testid="commissions-chart">
                <h3 className="font-heading font-bold text-lg mb-4">Commission Earnings</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={commissionChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="amount" stroke="#FF3B30" strokeWidth={2} dot={{ fill: '#FF3B30' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {activeTab === 'clicks' && (
          <div className="border border-[#E4E4E7] bg-white overflow-x-auto" data-testid="clicks-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F5] text-left">
                <tr>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Shoe</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Store</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Region</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Price</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Commission</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Status</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody>
                {clicks.map((c, i) => (
                  <tr key={i} className="border-t border-[#E4E4E7] hover:bg-[#F4F4F5]" data-testid={`click-row-${i}`}>
                    <td className="p-3 font-medium">{c.shoe_name}</td>
                    <td className="p-3">{c.store_name}</td>
                    <td className="p-3">{c.store_region}</td>
                    <td className="p-3 font-bold">${c.price?.toFixed(2)}</td>
                    <td className="p-3">${c.potential_commission?.toFixed(2)}</td>
                    <td className="p-3">{c.converted ? <span className="text-[#10B981] font-bold">Converted</span> : <span className="text-[#52525B]">Pending</span>}</td>
                    <td className="p-3">
                      {!c.converted && (
                        <button onClick={() => simulateConversion(c.click_id)} className="text-xs bg-[#0A0A0A] text-white px-3 py-1 hover:bg-[#FF3B30] transition-colors" data-testid={`simulate-btn-${i}`}>
                          Simulate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'commissions' && (
          <div className="border border-[#E4E4E7] bg-white overflow-x-auto" data-testid="commissions-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F5] text-left">
                <tr>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Store</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Sale Amount</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Commission (3%)</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Status</th>
                  <th className="p-3 font-bold uppercase text-xs tracking-wide">Date</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c, i) => (
                  <tr key={i} className="border-t border-[#E4E4E7] hover:bg-[#F4F4F5]" data-testid={`commission-row-${i}`}>
                    <td className="p-3 font-medium">{c.store_name}</td>
                    <td className="p-3">${c.sale_amount?.toFixed(2)}</td>
                    <td className="p-3 font-bold text-[#FF3B30]">${c.commission_amount?.toFixed(2)}</td>
                    <td className="p-3">{c.status === 'paid' ? <span className="text-[#10B981] font-bold">Paid</span> : <span className="text-[#F59E0B] font-bold">Pending</span>}</td>
                    <td className="p-3 text-[#52525B]">{c.created_at?.split('T')[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="border border-[#E4E4E7] bg-white overflow-x-auto" data-testid="users-table">
              <h3 className="font-heading font-bold text-lg p-4 border-b border-[#E4E4E7]">Registered Users</h3>
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F5] text-left">
                  <tr>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Name</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Email</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Role</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Fit Profile</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.users?.map((u, i) => (
                    <tr key={i} className="border-t border-[#E4E4E7] hover:bg-[#F4F4F5]">
                      <td className="p-3 font-medium">{u.name || '-'}</td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3 capitalize">{u.role}</td>
                      <td className="p-3">{u.fit_profile ? 'Yes' : 'No'}</td>
                      <td className="p-3 text-[#52525B]">{u.created_at?.split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border border-[#E4E4E7] bg-white overflow-x-auto" data-testid="customer-data-table">
              <h3 className="font-heading font-bold text-lg p-4 border-b border-[#E4E4E7]">Customer Data (Marketing)</h3>
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F5] text-left">
                  <tr>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Email</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Clicks</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {users.customer_data?.map((cd, i) => (
                    <tr key={i} className="border-t border-[#E4E4E7] hover:bg-[#F4F4F5]">
                      <td className="p-3">{cd.email || '-'}</td>
                      <td className="p-3 font-bold">{cd.click_count || 0}</td>
                      <td className="p-3 text-[#52525B]">{cd.last_activity?.split('T')[0] || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-6" data-testid="contacts-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-heading font-bold text-lg">Contact Messages</h3>
                <p className="text-sm text-[#52525B] mt-1">
                  <strong>{contactsData.total}</strong> total · <span className="text-[#FF3B30] font-bold">{contactsData.new_count} new</span>
                </p>
              </div>
              <a href={`${API}/admin/contacts/export.csv`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-[#FF3B30] transition-colors"
                data-testid="export-contacts-btn">
                <DownloadSimple size={14} weight="bold" /> Export CSV
              </a>
            </div>
            <div className="border border-[#E4E4E7] bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F5] text-left">
                  <tr>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Received</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Name</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Email</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Topic</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Message</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Status</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {contactsData.items.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-sm text-[#52525B]">No contact messages yet.</td></tr>
                  )}
                  {contactsData.items.map(c => (
                    <tr key={c.id} className="border-t border-[#E4E4E7] align-top" data-testid={`contact-row-${c.id}`}>
                      <td className="p-3 text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                      <td className="p-3 text-sm font-bold">{c.name}</td>
                      <td className="p-3 text-xs"><a href={`mailto:${c.email}?subject=Re: your SoleFinder enquiry`} className="text-[#FF3B30] hover:underline">{c.email}</a></td>
                      <td className="p-3 text-xs uppercase tracking-wide">{c.topic}</td>
                      <td className="p-3 text-xs text-[#52525B] max-w-[360px] whitespace-pre-wrap">{c.message}</td>
                      <td className="p-3 text-xs">
                        <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${c.status === 'replied' ? 'bg-[#F0FDF4] text-[#10B981]' : 'bg-[#FFF5F4] text-[#FF3B30]'}`}>{c.status || 'new'}</span>
                      </td>
                      <td className="p-3 text-xs">
                        {c.status !== 'replied' && (
                          <button
                            type="button"
                            onClick={async () => {
                              await axios.post(`${API}/admin/contacts/${c.id}/mark-replied`, {}, { withCredentials: true });
                              const r = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
                              setContactsData(r.data);
                            }}
                            className="text-[11px] font-bold uppercase tracking-wide text-[#52525B] hover:text-[#10B981] transition-colors"
                            data-testid={`mark-replied-${c.id}`}
                          >
                            Mark replied
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6" data-testid="subscribers-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-heading font-bold text-lg">Email Subscribers</h3>
                <p className="text-sm text-[#52525B] mt-1">
                  <strong>{subscribersData.active_count}</strong> active · <span className="text-[#A1A1AA]">{subscribersData.total} total</span>
                </p>
              </div>
              <a href={`${API}/admin/subscribers/export.csv`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-[#FF3B30] transition-colors"
                data-testid="export-subscribers-btn">
                <DownloadSimple size={14} weight="bold" /> Export CSV
              </a>
            </div>
            {Object.keys(subscribersData.by_source).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="subscribers-by-source">
                {Object.entries(subscribersData.by_source).map(([k, v]) => (
                  <span key={k} className="text-xs bg-[#F4F4F5] px-3 py-1.5 font-medium">
                    <span className="font-bold uppercase tracking-wide text-[#0A0A0A]">{k}:</span> {v}
                  </span>
                ))}
              </div>
            )}
            <div className="border border-[#E4E4E7] bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F5] text-left">
                  <tr>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Email</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Subscribed</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Source</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribersData.items.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-sm text-[#52525B]">No subscribers yet. Share your homepage — subscribers will show up here.</td></tr>
                  )}
                  {subscribersData.items.map((s, i) => (
                    <tr key={`${s.email}-${i}`} className="border-t border-[#E4E4E7]" data-testid={`subscriber-row-${s.email}`}>
                      <td className="p-3 text-sm"><a href={`mailto:${s.email}`} className="text-[#FF3B30] hover:underline">{s.email}</a></td>
                      <td className="p-3 text-xs whitespace-nowrap">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleString() : '—'}</td>
                      <td className="p-3 text-xs uppercase tracking-wide">{s.source || '—'}</td>
                      <td className="p-3 text-xs">{s.active !== false ? '✅' : '❌'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="space-y-6" data-testid="export-tab">
            <div className="border border-[#E4E4E7] bg-white p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Export Data as CSV</h3>
              <p className="text-sm text-[#52525B] mb-6">Download customer data, affiliate clicks, and commission records for analysis and marketing.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button onClick={() => handleExport('customers')} className="flex items-center gap-3 border-2 border-[#0A0A0A] p-4 hover:bg-[#F4F4F5] transition-colors" data-testid="export-customers-btn">
                  <DownloadSimple size={24} weight="duotone" className="text-[#FF3B30]" />
                  <div className="text-left"><p className="font-bold text-sm uppercase">Customers</p><p className="text-xs text-[#52525B]">Names, emails, fit profiles, activity</p></div>
                </button>
                <button onClick={() => handleExport('clicks')} className="flex items-center gap-3 border-2 border-[#0A0A0A] p-4 hover:bg-[#F4F4F5] transition-colors" data-testid="export-clicks-btn">
                  <DownloadSimple size={24} weight="duotone" className="text-[#FF3B30]" />
                  <div className="text-left"><p className="font-bold text-sm uppercase">Affiliate Clicks</p><p className="text-xs text-[#52525B]">Shoes, stores, prices, conversions</p></div>
                </button>
                <button onClick={() => handleExport('commissions')} className="flex items-center gap-3 border-2 border-[#0A0A0A] p-4 hover:bg-[#F4F4F5] transition-colors" data-testid="export-commissions-btn">
                  <DownloadSimple size={24} weight="duotone" className="text-[#FF3B30]" />
                  <div className="text-left"><p className="font-bold text-sm uppercase">Commissions</p><p className="text-xs text-[#52525B]">Sales, commission amounts, status</p></div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Campaign Tab */}
        {activeTab === 'email' && (
          <div className="space-y-6" data-testid="email-tab">
            <div className="border border-[#E4E4E7] bg-white p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Send Email Campaign</h3>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Audience</label>
                  <div className="flex gap-2 flex-wrap">
                    {[{ v: 'all', l: 'All Users' }, { v: 'wishlisted', l: 'Wishlist Users' }, { v: 'clicked', l: 'Active Clickers' }].map(opt => (
                      <button key={opt.v} onClick={() => setEmailForm({ ...emailForm, recipient_type: opt.v })}
                        className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${emailForm.recipient_type === opt.v ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
                        data-testid={`audience-${opt.v}`}>{opt.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Subject</label>
                  <input type="text" value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] outline-none" placeholder="e.g. New Arrivals This Week!" data-testid="email-subject" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">HTML Content <span className="normal-case font-normal">(use {"{{name}}"} for personalization)</span></label>
                  <textarea value={emailForm.html_content} onChange={e => setEmailForm({ ...emailForm, html_content: e.target.value })} rows={8}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] outline-none font-mono text-sm" 
                    placeholder="<h1>Hi {{name}},</h1><p>Check out our latest recommendations...</p>" data-testid="email-content" />
                </div>
                <button onClick={handleSendCampaign} disabled={sendingEmail}
                  className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="send-campaign-btn">
                  {sendingEmail ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                  {sendingEmail ? 'Sending...' : 'Send Campaign'}
                </button>
              </div>
            </div>
            {campaigns.length > 0 && (
              <div className="border border-[#E4E4E7] bg-white overflow-x-auto" data-testid="campaigns-table">
                <h3 className="font-heading font-bold text-lg p-4 border-b border-[#E4E4E7]">Past Campaigns</h3>
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F4F5] text-left"><tr>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Date</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Subject</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Audience</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Recipients</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Sent</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Status</th>
                  </tr></thead>
                  <tbody>
                    {campaigns.map((c, i) => (
                      <tr key={i} className="border-t border-[#E4E4E7] hover:bg-[#F4F4F5]">
                        <td className="p-3 text-[#52525B]">{c.created_at?.split('T')[0]}</td>
                        <td className="p-3 font-medium">{c.subject}</td>
                        <td className="p-3 capitalize">{c.recipient_type}</td>
                        <td className="p-3">{c.total_recipients}</td>
                        <td className="p-3 font-bold">{c.sent}</td>
                        <td className="p-3">{c.sendgrid_configured ? <span className="text-[#10B981] font-bold">Delivered</span> : <span className="text-[#F59E0B] font-bold">Pending Key</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Price Scraping Tab */}
        {activeTab === 'prices' && (
          <div className="space-y-6" data-testid="prices-tab">
            <div className="border border-[#E4E4E7] bg-white p-6">
              <h3 className="font-heading font-bold text-lg mb-2">Price Refresh & Scraping</h3>
              <p className="text-sm text-[#52525B] mb-6">Refresh prices from all 15 stores across AU, UK, and US. This will detect price drops and send alerts to users with matching wishlist items.</p>
              <button onClick={handlePriceRefresh} disabled={refreshing}
                className="bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#FF3B30] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="refresh-prices-btn">
                {refreshing ? <CircleNotch size={18} className="animate-spin" /> : <ArrowClockwise size={18} />}
                {refreshing ? 'Refreshing Prices...' : 'Refresh All Prices'}
              </button>
              {refreshResult && (
                <div className="mt-4 bg-[#F4F4F5] border border-[#E4E4E7] p-4" data-testid="refresh-result">
                  <p className="font-bold">Refresh Complete</p>
                  <p className="text-sm text-[#52525B]">{refreshResult.updated} prices updated &middot; {refreshResult.price_drops} price drops detected</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product Scraping Tab */}
        {activeTab === 'scraping' && (
          <ScrapingTab
            scraping={scraping}
            scrapeStatus={scrapeStatus}
            onScrapeAll={handleScrapeAll}
            onCleanupImages={handleCleanupImages}
            validating={validating}
            validateStatus={validateStatus}
            onValidateAllLinks={handleValidateAllLinks}
            priceScraping={priceScraping}
            priceStatus={priceStatus}
            onScrapeRealPrices={handleScrapeRealPrices}
            auScraping={auScraping}
            auStatus={auStatus}
            onScrapeAuStores={handleScrapeAuStores}
          />
        )}

        {/* Affiliate Settings Tab */}
        {activeTab === 'affiliate' && (
          <div className="space-y-6" data-testid="affiliate-tab">
            {/* Add new store form */}
            <div className="border-2 border-[#FF3B30] bg-white p-6" data-testid="add-store-form">
              <h3 className="font-heading font-bold text-lg mb-2">Add New Retailer</h3>
              <p className="text-sm text-[#52525B] mb-4">
                Use this when an affiliate network sends you an approval email. Fill in the details from the email,
                save, then go to the <strong>Awin</strong> tab (or import the products manually) to start tracking.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Retailer name *</label>
                  <input
                    type="text" value={newStoreForm.name}
                    onChange={e => setNewStoreForm({ ...newStoreForm, name: e.target.value })}
                    placeholder="e.g. Debenhams UK"
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                    data-testid="add-store-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Store ID * <span className="text-[10px] font-normal normal-case text-[#A1A1AA]">(lowercase, hyphens)</span></label>
                  <input
                    type="text" value={newStoreForm.store_id}
                    onChange={e => setNewStoreForm({ ...newStoreForm, store_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder="e.g. debenhams-uk"
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm font-mono"
                    data-testid="add-store-id"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Region *</label>
                  <select
                    value={newStoreForm.region}
                    onChange={e => setNewStoreForm({ ...newStoreForm, region: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                    data-testid="add-store-region"
                  >
                    <option value="UK">United Kingdom</option>
                    <option value="US">United States</option>
                    <option value="AU">Australia</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Currency *</label>
                  <select
                    value={newStoreForm.currency}
                    onChange={e => setNewStoreForm({ ...newStoreForm, currency: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                    data-testid="add-store-currency"
                  >
                    <option value="GBP">GBP — British Pound</option>
                    <option value="USD">USD — US Dollar</option>
                    <option value="AUD">AUD — Australian Dollar</option>
                    <option value="CAD">CAD — Canadian Dollar</option>
                    <option value="EUR">EUR — Euro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Affiliate network</label>
                  <select
                    value={newStoreForm.affiliate_network}
                    onChange={e => setNewStoreForm({ ...newStoreForm, affiliate_network: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                    data-testid="add-store-network"
                  >
                    <option value="awin">AWIN</option>
                    <option value="cj">CJ Affiliate</option>
                    <option value="rakuten">Rakuten</option>
                    <option value="commission_factory">Commission Factory (AU)</option>
                    <option value="impact">Impact</option>
                    <option value="amazon_associates">Amazon Associates</option>
                    <option value="direct">Direct / Brand Program</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Advertiser / Merchant ID</label>
                  <input
                    type="text" value={newStoreForm.affiliate_advertiser_id}
                    onChange={e => setNewStoreForm({ ...newStoreForm, affiliate_advertiser_id: e.target.value })}
                    placeholder="from the approval email"
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm font-mono"
                    data-testid="add-store-advertiser-id"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Website URL <span className="text-[10px] font-normal normal-case text-[#A1A1AA]">(optional)</span></label>
                  <input
                    type="url" value={newStoreForm.website_url}
                    onChange={e => setNewStoreForm({ ...newStoreForm, website_url: e.target.value })}
                    placeholder="https://www.debenhams.com"
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                    data-testid="add-store-website-url"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Search URL pattern <span className="text-[10px] font-normal normal-case text-[#A1A1AA]">(optional)</span></label>
                  <input
                    type="url" value={newStoreForm.search_url}
                    onChange={e => setNewStoreForm({ ...newStoreForm, search_url: e.target.value })}
                    placeholder="https://.../search?q={query}"
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                    data-testid="add-store-search-url"
                  />
                </div>
              </div>
              <button
                onClick={handleCreateStore}
                disabled={savingNewStore}
                className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#D62828] transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
                data-testid="add-store-save-btn"
              >
                {savingNewStore ? <CircleNotch size={14} className="animate-spin" /> : null}
                {savingNewStore ? 'Saving...' : 'Add Retailer'}
              </button>
              {newStoreMsg && (
                <div
                  className={`mt-4 p-3 text-xs border ${newStoreMsg.kind === 'ok' ? 'bg-[#F0FDF4] border-[#10B981] text-[#065F46]' : 'bg-[#FFF5F4] border-[#FF3B30] text-[#7C2D12]'}`}
                  data-testid={`add-store-${newStoreMsg.kind}`}
                >
                  {newStoreMsg.text}
                </div>
              )}
            </div>

            <div className="border border-[#E4E4E7] bg-white p-6">
              <h3 className="font-heading font-bold text-lg mb-2">Affiliate Tag Settings</h3>
              <p className="text-sm text-[#52525B] mb-2">Configure affiliate tracking per store. Once set, all "Shop" links for that retailer auto-wrap through the network's tracking domain.</p>
              <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-3 mb-4 text-xs text-[#52525B] space-y-1">
                <p><strong>Amazon Associates:</strong> field expects your tag (e.g. <code className="bg-white px-1">solefinder-20</code>)</p>
                <p><strong>CJ Affiliate / Awin / Rakuten / Impact:</strong> field expects the <strong>advertiser/merchant ID</strong> (a number, e.g. <code className="bg-white px-1">6284907</code>) from the approval email — NOT the word "cj"</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mb-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Store</label>
                  <select value={affiliateForm.store_id} onChange={e => setAffiliateForm({ ...affiliateForm, store_id: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="affiliate-store-select">
                    <option value="">Select store...</option>
                    {allStores.map(s => <option key={s.store_id} value={s.store_id}>{s.name} ({s.region})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Advertiser ID / Tag</label>
                  <input type="text" value={affiliateForm.affiliate_tag} onChange={e => setAffiliateForm({ ...affiliateForm, affiliate_tag: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" placeholder="e.g. 6284907 or solefinder-20" data-testid="affiliate-tag-input" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Network</label>
                  <select value={affiliateForm.network} onChange={e => setAffiliateForm({ ...affiliateForm, network: e.target.value })}
                    className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="affiliate-network-select">
                    <option value="">Select network...</option>
                    <option value="amazon_associates">Amazon Associates</option>
                    <option value="cj">CJ Affiliate</option>
                    <option value="awin">AWIN</option>
                    <option value="shareasale">ShareASale</option>
                    <option value="rakuten">Rakuten</option>
                    <option value="commission_factory">Commission Factory</option>
                    <option value="impact">Impact</option>
                    <option value="direct">Direct / Brand Program</option>
                  </select>
                </div>
              </div>
              <button onClick={handleSaveAffiliate}
                className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#D62828] transition-colors text-sm" data-testid="save-affiliate-btn">
                Save Affiliate Tag
              </button>
            </div>
            {affiliateSettings.length > 0 && (
              <div className="border border-[#E4E4E7] bg-white overflow-x-auto" data-testid="affiliate-settings-table">
                <h3 className="font-heading font-bold text-lg p-4 border-b border-[#E4E4E7]">Configured Tags ({affiliateSettings.length})</h3>
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F4F5] text-left"><tr>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Store</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Affiliate Tag</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Network</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Updated</th>
                    <th className="p-3 font-bold uppercase text-xs tracking-wide">Action</th>
                  </tr></thead>
                  <tbody>
                    {affiliateSettings.map((s, i) => (
                      <tr key={i} className="border-t border-[#E4E4E7] hover:bg-[#F4F4F5]">
                        <td className="p-3 font-medium">{s.store_id}</td>
                        <td className="p-3 font-mono text-xs bg-[#F4F4F5]">{s.affiliate_tag}</td>
                        <td className="p-3 capitalize">{s.network?.replace('_', ' ') || '-'}</td>
                        <td className="p-3 text-[#52525B]">{s.updated_at?.split('T')[0] || '-'}</td>
                        <td className="p-3">
                          <button onClick={() => handleDeleteAffiliate(s.store_id)} className="text-xs text-[#FF3B30] font-bold uppercase hover:underline" data-testid={`delete-affiliate-${s.store_id}`}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Scheduler Tab */}
        {activeTab === 'scheduler' && (
          <div className="space-y-6" data-testid="scheduler-tab">
            <div className="border border-[#E4E4E7] bg-white p-6">
              <h3 className="font-heading font-bold text-lg mb-2">Auto-Scraping Scheduler</h3>
              <p className="text-sm text-[#52525B] mb-6">Configure automatic periodic scraping of product images, prices, and store link verification.</p>
              <div className="space-y-4 max-w-md">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={schedulerConfig.enabled} onChange={e => setSchedulerConfig({...schedulerConfig, enabled: e.target.checked})}
                    className="w-5 h-5 accent-[#FF3B30]" data-testid="scheduler-enabled" />
                  <span className="font-bold">Enable Auto-Scraping</span>
                </label>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#52525B] mb-1">Image Scrape Interval (hours)</label>
                  <input type="number" value={schedulerConfig.scrape_interval_hours} min={1} max={168}
                    onChange={e => setSchedulerConfig({...schedulerConfig, scrape_interval_hours: parseInt(e.target.value) || 24})}
                    className="border-2 border-[#E4E4E7] px-4 py-2 w-full font-bold focus:border-[#0A0A0A] outline-none" data-testid="scrape-interval" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-[#52525B] mb-1">Store Verify Interval (hours)</label>
                  <input type="number" value={schedulerConfig.verify_interval_hours} min={1} max={336}
                    onChange={e => setSchedulerConfig({...schedulerConfig, verify_interval_hours: parseInt(e.target.value) || 48})}
                    className="border-2 border-[#E4E4E7] px-4 py-2 w-full font-bold focus:border-[#0A0A0A] outline-none" data-testid="verify-interval" />
                </div>
                <button onClick={handleSaveScheduler}
                  className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors"
                  data-testid="save-scheduler-btn">
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link Audit Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6" data-testid="audit-tab">
            <div className="border border-[#E4E4E7] bg-white p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Affiliate Link Audit</h3>
              {!linkAudit ? (
                <button onClick={fetchLinkAudit}
                  className="bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#52525B] transition-colors"
                  data-testid="run-audit-btn">
                  Run Link Audit
                </button>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="border border-[#E4E4E7] p-4 text-center">
                      <p className="text-3xl font-black">{linkAudit.link_summary?.total_links}</p>
                      <p className="text-xs text-[#52525B] uppercase font-bold mt-1">Total Links</p>
                    </div>
                    <div className="border border-[#E4E4E7] p-4 text-center">
                      <p className="text-3xl font-black text-[#10B981]">{linkAudit.link_summary?.verified}</p>
                      <p className="text-xs text-[#52525B] uppercase font-bold mt-1">Verified</p>
                    </div>
                    <div className="border border-[#E4E4E7] p-4 text-center">
                      <p className="text-3xl font-black">{linkAudit.affiliate_coverage?.stores_with_affiliate_tags}/{linkAudit.affiliate_coverage?.total_stores}</p>
                      <p className="text-xs text-[#52525B] uppercase font-bold mt-1">Stores w/ Tags</p>
                    </div>
                    <div className="border border-[#E4E4E7] p-4 text-center">
                      <p className="text-3xl font-black">{linkAudit.link_summary?.verification_rate}%</p>
                      <p className="text-xs text-[#52525B] uppercase font-bold mt-1">Verification Rate</p>
                    </div>
                  </div>
                  {linkAudit.store_performance?.length > 0 && (
                    <div>
                      <h4 className="font-heading font-bold text-base mb-3 uppercase">Top Stores by Clicks</h4>
                      <div className="border border-[#E4E4E7] overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="bg-[#F4F4F5] text-xs uppercase text-[#52525B] font-bold">
                            <th className="p-3 text-left">Store</th><th className="p-3 text-left">Region</th>
                            <th className="p-3 text-right">Clicks</th><th className="p-3 text-right">Conversions</th>
                            <th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Commission</th>
                          </tr></thead>
                          <tbody>
                            {linkAudit.store_performance.map((s, i) => (
                              <tr key={i} className="border-t border-[#E4E4E7]">
                                <td className="p-3 font-bold">{s.store_name}</td>
                                <td className="p-3">{s.store_region}</td>
                                <td className="p-3 text-right">{s.total_clicks}</td>
                                <td className="p-3 text-right">{s.conversions}</td>
                                <td className="p-3 text-right">${s.total_revenue?.toFixed(2)}</td>
                                <td className="p-3 text-right text-[#10B981]">${s.total_commission?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <button onClick={fetchLinkAudit}
                    className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] flex items-center gap-1"
                    data-testid="refresh-audit-btn">
                    <ArrowClockwise size={14} /> Refresh Audit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Store Links Management Tab */}
        {activeTab === 'links' && (
          <LinksTab
            shoesNoStores={shoesNoStores}
            onFetchNoStores={fetchShoesNoStores}
            newLink={newLink}
            setNewLink={setNewLink}
            onAddLink={handleAddLink}
          />
        )}

        {/* Awin product-feed importer */}
        {activeTab === 'awin' && <AwinTab />}

        {/* Unified affiliate performance dashboard + sync settings */}
        {activeTab === 'performance' && <AffiliateSyncTab />}

        {/* Affiliate networks (Sovrn + pending CF approvals) */}
        {activeTab === 'networks' && <NetworksTab />}

        {/* Affiliate application tracker */}
        {activeTab === 'applications' && <ApplicationsTab />}

        {/* Seed Catalog Tab */}
        {activeTab === 'seed' && (
          <SeedTab
            seedingKey={seedingKey}
            seedResults={seedResults}
            onRunSeed={handleRunSeed}
          />
        )}
      </div>
    </div>
  );
}
